import React, { FC, useMemo, useState } from 'react'

import { faCopy } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline'
import { ArrowDownOnSquareIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import NumberFormat from 'react-number-format'
import Select from 'react-select'
import ReactTooltip from 'react-tooltip'
import * as Yup from 'yup'

import { setScrollTo, toggleIsModalOpen } from '../AddTradeline/addTradelineSlice'
import TradelinesGroups from './TradelinesGroups'
import { accountTypeOptions, creditLoanTypeOptions } from './applicationSlice'
import {
  setPullCreditModalAction,
  setPullCreditModalOpen,
  setPullCreditModalUser,
  setViewTradelinesModalOpen,
} from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import CustomNumberFormat from '@/components/CustomFields/components/CustomNumberFormat'
import { InputField, NumberField, SelectField } from '@/components/FormikInputs'
import LoadingIcon from '@/components/LoadingIcon'
import { Application, ApplicationCalculationsType } from '@/graphql/generated'
import { useGetApplicationTradelinesCalcsQuery } from '@/graphql/services/applicationCalcs'
import KeyModal from '@/key-components/KeyModal'
import { KeySelectField } from '@/key-components/formik/KeySelectField'
import { useGetPropertyQuery, useUpdatePropertyMutation } from '@/services/property'
import { useCreateTradelineMutation } from '@/services/tradeline'
import { useGetTradelinesModalByApplicationQuery } from '@/services/tradelines'
import { PropertyForm } from '@/types/requests/property'
import { Tradeline } from '@/types/unlock'

export type ViewTradelinesModalProps = {
  application: Application
  calcs: ApplicationCalculationsType
  isLoadingCalcs: boolean
}

interface SortingConfiguration {
  propertyName: string
  sortType: SortingType
  parseNumber: boolean
}

export enum SortingType {
  Ascending,
  Descending,
}

export const ViewTradelinesModal: FC<ViewTradelinesModalProps> = (props) => {
  const { application, calcs } = props

  const { data: applicationTradelinesCalcsResponse } = useGetApplicationTradelinesCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application,
    }
  )

  const applicationTradelinesCalcs = useMemo(
    () => applicationTradelinesCalcsResponse?.application?.tradelinesCalculations,
    [applicationTradelinesCalcsResponse]
  )

  const [creationRow, setCreationRow] = useState(false)

  const showDTI =
    calcs?.calcApplicationFico < 550 && application?.property?.propertyUsage !== 'investment'

  const dispatch = useAppDispatch()
  const modalOpen = useAppSelector((state) => state.general.viewTradelinesModalOpen)

  const [updateProperty] = useUpdatePropertyMutation()
  const [createTradeline] = useCreateTradelineMutation()

  const { data: tradelinesPage } = useGetTradelinesModalByApplicationQuery(application?.uuid, {
    skip: !application || !modalOpen,
  })

  const [sortConfig, updateSortConfig] = React.useState<SortingConfiguration[]>([
    { propertyName: 'pmt_ratio', sortType: SortingType.Ascending, parseNumber: true },
  ])

  const { data: property } = useGetPropertyQuery(application?.uuid ?? '', {
    skip: !application?.uuid,
  })

  const tradelines = tradelinesPage?.results || []
  interface IparsedTradelines {
    mortgage: Tradeline[]
    other: Tradeline[]
    all: Tradeline[]
  }

  const parsedTradelines: IparsedTradelines = tradelines.reduce(
    (acc, currTradeline, idx) => {
      const isMorgageOrHeloc =
        currTradeline.account_type == 'Mortgage' || currTradeline.account_type == 'HELOC'

      const key = isMorgageOrHeloc ? 'mortgage' : 'other'

      acc[key].push(currTradeline)

      return acc
    },
    { mortgage: [], other: [], all: [] }
  )

  const groupedTradelines = tradelines.reduce((acc, currTradeline, idx) => {
    let key
    if (
      !currTradeline?.account_type ||
      currTradeline?.account_type === 'Unknown' ||
      currTradeline?.account_type === ''
    ) {
      key = 'unknown'
    } else {
      key = currTradeline.account_type?.toLowerCase()
    }

    if (!(key in acc)) {
      acc[key] = []
    }

    acc[key].push(currTradeline)
    return acc
  }, {})

  parsedTradelines.all = parsedTradelines.mortgage.concat(parsedTradelines.other)
  const hasMortgageTradelines = parsedTradelines.mortgage.length !== 0
  const missingMonthlyDebts =
    !application?.property?.monthlyCostTaxes ||
    !application?.property?.monthlyCostInsuranceHazard ||
    !application?.property?.monthlyCostHoa

  const sortBy = React.useCallback(
    (propertyName: string, parseNumber = false) => {
      let pendingChange = [...sortConfig]
      const index = pendingChange.findIndex((config) => config.propertyName === propertyName)
      if (index > -1) {
        const currentSortType = pendingChange[index].sortType
        pendingChange.splice(index, 1)

        if (currentSortType === SortingType.Descending) {
          pendingChange = [
            { propertyName: propertyName, sortType: SortingType.Ascending, parseNumber },
          ]
        }
      } else {
        pendingChange = [
          { propertyName: propertyName, sortType: SortingType.Descending, parseNumber },
        ]
      }
      updateSortConfig([...pendingChange])
    },
    [sortConfig]
  )

  const getSortDirection = (property: string) => {
    const config = sortConfig.find((sortConfig) => sortConfig.propertyName === property)

    if (config) {
      const className = 'absolute right-3 top-3 text-gray-400'

      return config.sortType === SortingType.Descending ? (
        <ArrowDownIcon
          width={18}
          className={className}
        />
      ) : (
        <ArrowUpIcon
          width={18}
          className={className}
        />
      )
    }

    return null
  }

  const handleAddTradeline = () => {
    dispatch(setViewTradelinesModalOpen(false))
    dispatch(toggleIsModalOpen())
    dispatch(setScrollTo('financing'))
  }

  return (
    <KeyModal
      isModalOpen={modalOpen}
      title='Tradelines DTI Calculator'
      onModalClose={() => dispatch(setViewTradelinesModalOpen(false))}
    >
      {/* Tradelines Table */}
      <div className={`mt-6 ${showDTI ? 'flex' : ''}`}>
        {tradelines.length == 0 ? (
          <div className='w-full'>
            <span className='text-center'>
              This application does not have tradelines. Import tradelines to view
            </span>
            <button
              className='mt-3 text-blue-600 flex'
              onClick={() => {
                dispatch(setPullCreditModalOpen(true))
                dispatch(setPullCreditModalAction(''))
                dispatch(setPullCreditModalUser(''))
              }}
            >
              Import Tradelines
            </button>
          </div>
        ) : (
          <>
            <div className='w-full mr-4'>
              <div className='w-full border rounded'>
                <div className='flex items-center m-4'>
                  <FontAwesomeIcon
                    width={18}
                    icon={faCopy}
                    color='#B4B6BA'
                  />
                  <h1 className='ml-2 text-xl'>Tradelines</h1>
                </div>
                <div className={`h-[500px] overflow-y-auto`}>
                  <table className='table-fixed	w-full'>
                    <thead
                      className='uppercase sticky top-0 border-y text-sky-400 bg-[#F5F9FC] font-bold relative'
                      style={{ width: 'calc( 100% - 1em )' }}
                    >
                      <tr className='text-xs'>
                        <th className='px-6 py-3 relative hover:cursor-pointer'>Type</th>
                        <th
                          className='px-6 py-3 text-left relative hover:cursor-pointer'
                          onClick={() => sortBy('credit_loan_type')}
                        >
                          Loan Type
                          {getSortDirection('credit_loan_type')}
                        </th>
                        <th
                          className='px-6 py-3 text-left relative hover:cursor-pointer'
                          onClick={() => sortBy('name')}
                        >
                          Creditor
                          {getSortDirection('name')}
                        </th>
                        <th
                          className='px-6 py-3 relative hover:cursor-pointer'
                          onClick={() => sortBy('unpaid_balance_amount', true)}
                        >
                          Current Balance
                          {getSortDirection('unpaid_balance_amount')}
                        </th>
                        <th
                          className='px-6 py-3 relative hover:cursor-pointer'
                          onClick={() => sortBy('monthly_payment_amount', true)}
                        >
                          Monthly Debt
                          {getSortDirection('monthly_payment_amount')}
                        </th>
                        <th
                          className='px-6 py-3 relative hover:cursor-pointer'
                          onClick={() => sortBy('pmt_ratio')}
                        >
                          PMT %{getSortDirection('pmt_ratio')}
                        </th>
                        <th
                          className='px-6 py-3 relative hover:cursor-pointer'
                          onClick={() => sortBy('is_subject_property')}
                        >
                          Is Subj. Property
                          {getSortDirection('is_subject_property')}
                        </th>
                        <th
                          className='px-6 py-3 relative hover:cursor-pointer'
                          onClick={() => sortBy('is_payoff')}
                        >
                          To be p/o
                          {getSortDirection('is_payoff')}
                        </th>
                        <th
                          className='px-6 py-3 relative hover:cursor-pointer'
                          onClick={() => sortBy('is_subordinated')}
                        >
                          To be sub
                          {getSortDirection('is_subordinated')}
                        </th>
                        <th
                          className='px-6 py-3 relative hover:cursor-pointer'
                          onClick={() => sortBy('manual_update_indicator')}
                        >
                          Ignore
                          {getSortDirection('manual_update_indicator')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <TradelinesGroups
                        groupedTradelines={groupedTradelines}
                        application={application}
                        sortConfig={sortConfig}
                      />
                    </tbody>
                  </table>
                </div>
              </div>
              {creationRow && (
                <Formik
                  enableReinitialize={true}
                  initialValues={{
                    name: '',
                    account_type: '',
                    credit_loan_type: '',
                    unpaid_balance_amount: null,
                    monthly_payment_amount: null,
                    account_identifier: '',
                    solar_terms_in_months: null,
                  }}
                  validationSchema={Yup.object().shape({
                    name: Yup.string().required('The name is required'),
                    account_type: Yup.string().required('The Account Type is required'),
                    credit_loan_type: Yup.string().required('The Credit Loan Type is required'),
                    unpaid_balance_amount: Yup.string()
                      .nullable()
                      .required('The Current Balance is required'),
                    monthly_payment_amount: Yup.string().required('The Monthly Debt is required'),
                    account_identifier: Yup.string().required('The Account Number is required'),
                    solar_terms_in_months: Yup.number()
                      .nullable()
                      .when('account_type', {
                        is: (account_type) => account_type === 'Solar',
                        then: (schema) => schema.required('Terms is required'),
                        otherwise: (schema) => schema.optional(),
                      }),
                  })}
                  onSubmit={async (values: Partial<Tradeline>) => {
                    await createTradeline({
                      ...values,
                      unpaid_balance_amount: values.unpaid_balance_amount,
                      account_status_type: 'Open',
                      credit_loan_type: values.credit_loan_type,
                      manual_update_indicator: true,
                      application: application?.id,
                      mortgage_for: property.id,
                      mortgage_debt: true,
                    }).unwrap()
                    setCreationRow(false)
                  }}
                >
                  {({
                    isSubmitting,
                    submitForm,
                    values,
                    setFieldValue,
                    setFieldTouched,
                    errors,
                  }) => (
                    <Form className='flex gap-4 mt-4 justify-start'>
                      <SelectField
                        extraClasses='flex-auto basis-48'
                        name='account_type'
                        label='Account Type'
                        options={accountTypeOptions}
                        readOnly={isSubmitting}
                      />
                      <div className='flex-auto basis-48'>
                        <label className='block mb-2 text-sm text-gray-400'>Credit Loan Type</label>
                        <Select
                          name='credit_loan_type'
                          options={creditLoanTypeOptions}
                          isSearchable
                          onChange={(option) => {
                            setFieldValue('credit_loan_type', option?.value)
                          }}
                          menuPlacement='top'
                          onMenuClose={() => {
                            setFieldTouched('credit_loan_type')
                          }}
                          styles={{
                            control: (styles) => ({
                              ...styles,
                              border: 0,
                              cursor: 'pointer',
                            }),
                          }}
                        />
                        {errors?.credit_loan_type && (
                          <ErrorMessage
                            component='span'
                            name='credit_loan_type'
                            className={`mt-1 text-error`}
                          />
                        )}
                      </div>
                      <InputField
                        parentClassName='flex-auto basis-48'
                        name='name'
                        label='Creditor'
                        readOnly={isSubmitting}
                        showError
                      />
                      <InputField
                        parentClassName='flex-auto basis-48'
                        name='account_identifier'
                        label='Account Number'
                        readOnly={isSubmitting}
                        showError={true}
                      />
                      <NumberField
                        parentClassName='flex-auto basis-48'
                        name='unpaid_balance_amount'
                        label='Current Balance'
                        readOnly={isSubmitting}
                        fixedDecimalScale
                        decimalScale={2}
                      />
                      <NumberField
                        parentClassName='flex-auto basis-48'
                        name='monthly_payment_amount'
                        label='Monthly Debt'
                        readOnly={isSubmitting}
                        fixedDecimalScale
                        decimalScale={2}
                      />
                      {values.account_type === 'Solar' && (
                        <>
                          <NumberField
                            parentClassName='flex-auto basis-48'
                            name='solar_terms_in_months'
                            label='Terms in Months'
                            readOnly={isSubmitting}
                            prefix=' ' // intentionally blank
                          />
                        </>
                      )}
                      <div className='flex items-center justify-center'>
                        <button
                          type='button'
                          disabled={isSubmitting}
                          className='py-2 px-2'
                          onClick={submitForm}
                        >
                          {isSubmitting ? (
                            <LoadingIcon
                              height={18}
                              width={18}
                            />
                          ) : (
                            <ArrowDownOnSquareIcon
                              className='text-gray-400'
                              width={22}
                              height={22}
                            />
                          )}
                        </button>
                        <button
                          type='button'
                          className='py-2 px-2'
                          onClick={() => setCreationRow(false)}
                          disabled={isSubmitting}
                        >
                          <XCircleIcon
                            className='text-gray-400'
                            width={22}
                            height={22}
                          />
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              )}
              <button
                className='my-4 underline text-gray-400'
                onClick={handleAddTradeline}
              >
                Add a tradeline
              </button>
            </div>

            {/* DTI Calculator */}

            <div className={`${showDTI ? 'w-[40%]' : ''}`}>
              {showDTI && (
                <div className='border rounded'>
                  <div className='flex items-center m-4'>
                    <FontAwesomeIcon
                      width={18}
                      icon={faCopy}
                      color='#B4B6BA'
                    />
                    <h1 className='ml-2 text-xl'>DTI Calculator</h1>
                  </div>
                  <div className='h-[550px] flex flex-col'>
                    <div>
                      <Formik
                        initialValues={{
                          external_id: property?.external_id,
                          uuid: property?.uuid,
                          monthly_cost_taxes: property?.monthly_cost_taxes,
                          monthly_cost_insurance_hazard: property?.monthly_cost_insurance_hazard,
                          monthly_cost_insurance_mortgage:
                            property?.monthly_cost_insurance_mortgage,
                          monthly_cost_hoa: property?.monthly_cost_hoa,
                          dti_escrow_status: property?.dti_escrow_status,
                        }}
                        onSubmit={async (values: Partial<PropertyForm>, { setSubmitting }) => {
                          await updateProperty({
                            ...values,
                          }).unwrap()
                          setSubmitting(false)
                        }}
                      >
                        {({ submitForm, values }) => (
                          <>
                            {!hasMortgageTradelines && missingMonthlyDebts && (
                              <ReactTooltip
                                id='monthly_debts'
                                place='bottom'
                              >
                                There are no mortgage tradelines. Enter the values to calculate DTI
                              </ReactTooltip>
                            )}
                            <table
                              data-for='monthly_debts'
                              data-tip
                              className={'overflow-auto text-sm block'}
                            >
                              <thead className='border-y font-bold text-sky-400 bg-sky-200/20'>
                                <tr>
                                  <th className='text-center py-3'>Taxes</th>
                                  <th className='text-center py-3'>Insurance</th>
                                  <th className='text-center py-3'>PMI</th>
                                  <th className='text-center py-3'>HOA</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className='text-center'>
                                  <td className='py-4 px-2'>
                                    <NumberField
                                      parentClassName='w-full m-auto border rounded'
                                      extraClassName='text-center text-xs !font-light hover:shadow-none'
                                      name='monthly_cost_taxes'
                                      readOnly={
                                        values.dti_escrow_status === 'taxes-ins' ||
                                        values.dti_escrow_status === 'taxes-only'
                                      }
                                      decimalScale={2}
                                      thousandSeparator={true}
                                      prefix='$'
                                      onBlur={submitForm}
                                    />
                                  </td>
                                  <td className='text-center px-2'>
                                    <NumberField
                                      parentClassName='w-full m-auto border rounded'
                                      extraClassName='text-center text-xs !font-light hover:shadow-none focus:border-none'
                                      name='monthly_cost_insurance_hazard'
                                      thousandSeparator={true}
                                      readOnly={
                                        values.dti_escrow_status === 'taxes-ins' ||
                                        values.dti_escrow_status === 'ins-only'
                                      }
                                      prefix='$'
                                      onBlur={submitForm}
                                    />
                                  </td>
                                  <td className='text-center px-2'>
                                    <NumberField
                                      parentClassName='w-full m-auto border rounded'
                                      extraClassName='text-center text-xs !font-light hover:shadow-none focus:border-none'
                                      name='monthly_cost_insurance_mortgage'
                                      decimalScale={2}
                                      readOnly
                                      thousandSeparator={true}
                                      prefix='$'
                                      onBlur={submitForm}
                                    />
                                  </td>
                                  <td className='text-center px-2'>
                                    <NumberField
                                      parentClassName='w-full m-auto border rounded'
                                      extraClassName='text-center text-xs !font-light hover:shadow-none focus:border-none'
                                      name='monthly_cost_hoa'
                                      decimalScale={2}
                                      thousandSeparator={true}
                                      prefix='$'
                                      onBlur={submitForm}
                                    />
                                  </td>
                                </tr>
                                <tr>
                                  <th className='text-center py-3 px-2 font-light text-sky-400'>
                                    Escrow Status
                                  </th>
                                </tr>
                              </tbody>
                            </table>
                            <div className='px-2'>
                              <Field
                                id='escrow-status'
                                name='dti_escrow_status'
                                label='escrow-status*'
                                placeholder='Select...'
                                component={KeySelectField}
                                className='w-1/5'
                                onBlur={submitForm}
                                options={[
                                  {
                                    label: 'Taxes & Ins',
                                    value: 'taxes-ins',
                                  },
                                  {
                                    label: 'Taxes only',
                                    value: 'taxes-only',
                                  },
                                  {
                                    label: 'Ins only',
                                    value: 'ins-only',
                                  },
                                  {
                                    label: 'Not Escrowed',
                                    value: 'not-escrowed',
                                  },
                                ]}
                              />
                            </div>
                          </>
                        )}
                      </Formik>
                      <table className='w-full mt-3 text-sm'>
                        <thead className='font-bold border-y text-sky-400 bg-sky-200/20'>
                          <tr>
                            <th className='py-3 text-left px-2'>Summary</th>
                            <th className='py-3 text-left text-center px-2'>Before Payoffs</th>
                            <th className='py-3 text-left text-center px-2'>Post Payoffs</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className='text-sky-400 py-3 text-xs px-2'>Total Monthly Income</td>
                            <td className='py-3 font-light text-center px-2'>
                              <NumberFormat
                                className='w-full border-none p-0 text-xs text-center'
                                value={applicationTradelinesCalcs?.totalMonthlyIncome}
                                prefix='$'
                                decimalScale={2}
                                thousandSeparator
                                readOnly={true}
                                disabled={true}
                              />
                            </td>
                            <td className='py-3 font-light text-center'>
                              <NumberFormat
                                className='w-full border-none p-0 text-xs text-center'
                                value={applicationTradelinesCalcs?.totalMonthlyIncome}
                                prefix='$'
                                decimalScale={2}
                                thousandSeparator
                                readOnly={true}
                                disabled={true}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className='text-sky-400 text-xs py-3 px-2'>Total Monthly Debt</td>
                            <td className='font-light py-3 text-center'>
                              <NumberFormat
                                className='w-full border-none p-0 text-xs text-center'
                                value={applicationTradelinesCalcs?.monthlyDebtPrePayoffs}
                                prefix='$'
                                decimalScale={2}
                                thousandSeparator
                                readOnly={true}
                                disabled={true}
                              />
                            </td>
                            <td className='font-light py-3 text-center'>
                              <NumberFormat
                                className='w-full border-none p-0 text-xs text-center'
                                value={applicationTradelinesCalcs?.monthlyDebt}
                                prefix='$'
                                decimalScale={2}
                                thousandSeparator
                                readOnly={true}
                                disabled={true}
                              />
                            </td>
                          </tr>
                          <tr className='border-b'>
                            <td className='text-sky-400 text-xs py-3 px-2'>
                              Monthly Debt to Reduce
                            </td>
                            <td className='font-light py-3 text-center'>
                              <CustomNumberFormat
                                className='w-full border-none p-0 text-xs text-center'
                                prefix='$'
                                value={applicationTradelinesCalcs?.monthlyDebtReduction}
                                decimalScale={2}
                                roundingSystem='up'
                                thousandSeparator
                                readOnly
                                disabled
                              />
                            </td>
                            <td className='font-light py-3 text-center'>
                              <NumberFormat
                                className='w-full border-none p-0 text-xs text-center'
                                value={applicationTradelinesCalcs?.monthlyPaymentAmountTotal || 0}
                                prefix='$'
                                decimalScale={2}
                                thousandSeparator
                                readOnly={true}
                                disabled={true}
                              />
                            </td>
                          </tr>
                          <tr className='bg-sky-200/20 border-b'>
                            <td className='py-8 text-sky-400 text-xs font-bold py-3 px-2'>
                              DTI
                              <span className='text-gray-400'>
                                {' '}
                                {calcs?.isDtiOverriden && '(Overriden)'}
                              </span>
                            </td>
                            <td className={`relative text-sm px-3`}>
                              <CustomNumberFormat
                                className={`w-full border rounded py-2 text-xs text-right ${
                                  calcs?.dtiPrePayoffs <= 0.45 ? 'border-success' : 'border-error'
                                }`}
                                value={calcs?.dtiPrePayoffs * 100}
                                suffix='%'
                                thousandSeparator
                                readOnly={true}
                                disabled={true}
                                decimalScale={2}
                              />
                              {calcs?.dtiPrePayoffs <= 0.45 ? (
                                <span className='font-light text-success text-left'>In range</span>
                              ) : (
                                <span className='font-light text-error text-left'>
                                  Out of range
                                </span>
                              )}
                            </td>
                            <td className={`relative text-sm px-3`}>
                              <CustomNumberFormat
                                className={`w-full border rounded py-2 text-xs text-right ${
                                  calcs?.dti <= 0.45 ? 'border-success' : 'border-error'
                                }`}
                                value={calcs?.dti * 100}
                                suffix='%'
                                thousandSeparator
                                readOnly={true}
                                disabled={true}
                                decimalScale={2}
                              />
                              {calcs?.dti <= 0.45 ? (
                                <span className='font-light text-success text-left'>In range</span>
                              ) : (
                                <span className='font-light text-error text-left'>
                                  Out of range
                                </span>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              <button
                className='w-full py-1 mt-6 text-center bg-[#202533] text-[#CFDFEF] rounded'
                onClick={() => {
                  dispatch(setPullCreditModalOpen(true))
                  dispatch(setPullCreditModalAction(''))
                  dispatch(setPullCreditModalUser(''))
                }}
              >
                Import Tradelines
              </button>
            </div>
          </>
        )}
      </div>
    </KeyModal>
  )
}
